import { FormApi } from "final-form";
import { useMemo, useRef } from "react";
import { Form, FormSpy } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { TextareaField } from "src/ui/shared/form/TextareaField";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { useCreateBuildMutation, useFetchProductBuildsQuery, useFetchProductChangeLogsQuery } from "@dashboard/products/api";
import { BuildConfig } from "@dashboard/products/api/apiClient";
import { Build, Product } from "@dashboard/products/types/index";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import { Box, DialogProps, Theme } from "@mui/material";
import SemverService from "@services/semverService/semverService";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { ControlledSelectValue } from "@shared/form/SelectField/SelectField.types";
import { TextField } from "@shared/form/TextField";
import { Tooltip } from "@shared/Tooltip";

import { BuildVariantLabel } from "../../../Builds/shared";
import { RolloutField, validateRollout } from "../../../shared/buildConfigFields";
import { buildVariantOptions } from "../Recipes";

const useStyles = makeStyles()((theme: Theme) => ({
    sectionBox: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "center",
    },
    inputContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        paddingTop: theme.spacing(2),
    },
    // the release notes field is several rows tall, so its hint icon lines up with the first row instead of the middle
    tallSectionBox: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "flex-start",
        "& > :last-child": {
            marginTop: theme.spacing(2),
        },
    },
}));

type Props = Omit<DialogProps, "onClose"> & {
    onClose: () => void;
    product: Product;
};
type FormValues = BuildConfig & {
    changeLogSection: string;
};

export const getNextVersion = (builds: Build[], androidVersion: string): string => {
    const relevantBuilds = builds.map((build) => build.version).sort(SemverService.compare);

    if (relevantBuilds.length === 0) {
        return `${androidVersion}.0.0`;
    }

    const latestVersion = relevantBuilds[relevantBuilds.length - 1];
    const nextVersion = SemverService.inc(latestVersion, "patch");

    return nextVersion || `${androidVersion}.0.0`;
};
const validate = (values: FormValues, androidVersion: string) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};

    const versionPattern = new RegExp(`^${androidVersion}\\.\\d+\\.\\d+$`);

    if (!versionPattern.test(values.version)) {
        errors.version = i18n.t("modals:osBuild.input.version.validation.format", { androidVersion });
    }

    const hasReleaseNotes = values.changeLogSection?.split("\n").some((line) => line.trim() !== "");
    if (!hasReleaseNotes) {
        errors.changeLogSection = i18n.t("modals:buildConfig.input.releaseNotes.validation.required");
    }

    const rolloutError = validateRollout(values.rolloutPercentage);
    if (rolloutError) {
        errors.rolloutPercentage = rolloutError;
    }
    return errors;
};

function getChangeLogByVersion(changeLog: Product["changes"] | undefined, version: string) {
    return changeLog?.[version]?.join("\n") || "";
}

function getPreselectedIncrementalFromVersions(builds: Build[]): string[] {
    const allVersions = builds
        .filter((build) => build.artifacts?.some((artifact) => artifact.type === "signed-files"))
        .map((build) => build.version)
        .filter((v): v is string => Boolean(v))
        .sort(SemverService.compare);

    // preselect two versions for incremental updates:
    // - the last release version (ending with .0)
    // - the most recent version (could be a nightly)
    if (allVersions.length === 0) {
        return [];
    }
    if (allVersions.length === 1) {
        return allVersions;
    }

    const lastReleaseVersion =
        allVersions
            .filter((v) => {
                const parsed = SemverService.parse(v);
                return parsed && parsed.patch === 0;
            })
            .sort(SemverService.compare)
            .pop() ?? null;

    const mostRecentVersion = allVersions[allVersions.length - 1];

    if (lastReleaseVersion === mostRecentVersion) {
        return allVersions.slice(-2);
    }

    if (lastReleaseVersion) {
        return [lastReleaseVersion, mostRecentVersion];
    }
    return allVersions.slice(-2);
}

export const ConfigurationModal = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "modals"]);

    const [createBuild] = useCreateBuildMutation();

    const { data = [] } = useFetchProductBuildsQuery(props.product.id);
    const builds = data.filter((build) => build.buildStatus === "succeeded");

    const { data: changeLog } = useFetchProductChangeLogsQuery(props.product.id);

    const productAndroidVersion = props.product.androidVersion.split(".")[0];

    const fromVersionBuilds = builds.filter(
        (build) => build.artifacts && build.artifacts.some((artifact) => artifact.type === "manifest"),
    );

    const preselectedVersion = getNextVersion(builds, productAndroidVersion);
    const prevVersionRef = useRef(preselectedVersion);
    const fromVersionOptions = fromVersionBuilds.map((build) => ({
        label: build.version || "",
        value: build.version || "",
    }));
    const incrementalFromVersionsOptions = builds
        .filter((build) => build.artifacts && build.artifacts.some((artifact) => artifact.type === "signed-files"))
        .map((build) => ({ label: build.version || "", value: build.version || "" }));

    const buildVariant = props.product.customBuildConfig?.buildVariant;
    const compatibility = props.product.customBuildConfig?.compatibility || "";
    const isIncrementalDisabled = props.product.customBuildConfig?.ota?.disableIncremental ?? false;

    const initialValues = useMemo(() => {
        const succeededBuilds = data.filter((build) => build.buildStatus === "succeeded");
        const version = getNextVersion(succeededBuilds, productAndroidVersion);
        return {
            version,
            changeLogSection: getChangeLogByVersion(changeLog, version),
            rolloutPercentage: 100,
            configOverride: {
                ota: {
                    incrementalFromVersions: isIncrementalDisabled ? [] : getPreselectedIncrementalFromVersions(succeededBuilds),
                },
                buildVariant,
                compatibility,
            },
        };
    }, [data, changeLog, productAndroidVersion, buildVariant, compatibility, isIncrementalDisabled]);

    const handleVersionChange = (value: ControlledSelectValue<false> | undefined, form: FormApi<FormValues>) => {
        if (value === undefined) {
            form.change("configSnapshot", undefined);
        }

        if (value && typeof value === "string") {
            const selectedBuild = fromVersionBuilds.find((build) => build.version === value);
            if (selectedBuild?.configSnapshot) {
                form.change("configSnapshot", selectedBuild.configSnapshot);
            }
        }
    };

    const onSubmit = async ({ changeLogSection, ...values }: FormValues) => {
        const filteredValues = { ...values };

        if (filteredValues.configOverride?.manifest?.fromVersion === undefined) {
            delete filteredValues.configOverride.manifest;
        }

        if (filteredValues.configOverride.buildVariant === props.product.customBuildConfig?.buildVariant) {
            delete filteredValues.configOverride.buildVariant;
        }

        if ((filteredValues.configOverride.compatibility || "") === (props.product.customBuildConfig?.compatibility || "")) {
            delete filteredValues.configOverride.compatibility;
        }

        // The field is disabled and starts out empty, but the product flag can be flipped while this
        // dialog is open - never submit stale base versions for a product that cannot build them.
        if (isIncrementalDisabled) {
            filteredValues.configOverride.ota = { incrementalFromVersions: [] };
        }

        const changes = changeLogSection?.split("\n").filter((item) => item.trim() !== "");

        try {
            await createBuild({
                productId: props.product.id,
                config: {
                    ...filteredValues,
                    changes,
                },
            });
        } finally {
            props.onClose();
        }
    };
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        const { submitting, active, pristine } = form.getState();

        if (e.key === "Enter" && !pristine && !submitting && !active) {
            e.preventDefault();
            await form.submit();
        }

        if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !pristine && !submitting && active === "changeLogSection") {
            e.preventDefault();
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            initialValues={initialValues}
            validate={(values) => validate(values, productAndroidVersion)}
            onSubmit={onSubmit}
            render={({ handleSubmit, submitting, form }) => (
                <Modal
                    {...props}
                    title={t("modals:osBuild.title")}
                    description={t("modals:osBuild.description")}
                    onKeyDown={(e) => onKeyDown(e, form)}
                    endButton={
                        <PrimaryButton onClick={form.submit} disabled={submitting} loading={submitting}>
                            {t("common:button.start")}
                        </PrimaryButton>
                    }
                >
                    <form onSubmit={handleSubmit} className={classes.inputContainer}>
                        <FormSpy<FormValues>
                            subscription={{ values: true }}
                            onChange={({ values }) => {
                                if (!values || values.version === prevVersionRef.current) {
                                    return;
                                }
                                prevVersionRef.current = values.version;
                                const notes = getChangeLogByVersion(changeLog, values.version);
                                if (notes) {
                                    form.change("changeLogSection", notes);
                                }
                            }}
                        />
                        <div className={classes.sectionBox}>
                            <TextField name="version" label={t("modals:osBuild.input.version.caption")} fullWidth />
                            <Tooltip title={t("modals:osBuild.tooltip.version")}>
                                <i className="fa-solid fa-circle-info" />
                            </Tooltip>
                        </div>

                        <div className={classes.tallSectionBox}>
                            <TextareaField
                                name="changeLogSection"
                                label={t("modals:buildConfig.input.releaseNotes.caption")}
                                minRows={4}
                                placeholder={t("modals:buildConfig.input.releaseNotes.placeholder")}
                                fullWidth
                            />
                            <Tooltip title={t("modals:buildConfig.tooltip.releaseNotes")}>
                                <i className="fa-solid fa-circle-info" />
                            </Tooltip>
                        </div>

                        <CollapsibleProvider>
                            <CollapsibleSectionSummary>
                                <CollapsibleSectionTitle size="medium">
                                    {t("modals:buildConfig.moreOptions")}
                                </CollapsibleSectionTitle>
                            </CollapsibleSectionSummary>
                            <CollapsibleSectionDetails>
                                <Box className={classes.inputContainer}>
                                    <div className={classes.sectionBox}>
                                        <ControlledSelectField
                                            name="configOverride.ota.incrementalFromVersions"
                                            label={t("modals:osBuild.input.incrementalUpdates.caption")}
                                            options={incrementalFromVersionsOptions}
                                            disabled={isIncrementalDisabled || !incrementalFromVersionsOptions.length}
                                            disabledTooltip={
                                                isIncrementalDisabled
                                                    ? t("modals:osBuild.tooltip.incrementalDisabledForProduct")
                                                    : t("modals:osBuild.tooltip.noVersions")
                                            }
                                            multiple
                                            fullWidth
                                        />
                                        <Tooltip title={t("modals:osBuild.tooltip.incrementalUpdates")}>
                                            <i className="fa-solid fa-circle-info" />
                                        </Tooltip>
                                    </div>

                                    <div className={classes.sectionBox}>
                                        <ControlledSelectField
                                            fullWidth
                                            name="configOverride.manifest.fromVersion"
                                            label={t("modals:osBuild.input.sourceConfig.caption")}
                                            options={fromVersionOptions}
                                            onChange={(value) => handleVersionChange(value, form)}
                                            disableClearable={false}
                                        />
                                        <Tooltip title={t("modals:osBuild.tooltip.sourceConfig")}>
                                            <i className="fa-solid fa-circle-info" />
                                        </Tooltip>
                                    </div>

                                    <div className={classes.sectionBox}>
                                        <TextField
                                            name="configOverride.compatibility"
                                            label={i18n.t("pages:productPage.input.compatibility.caption")}
                                            fullWidth
                                        />
                                        <Tooltip title={i18n.t("pages:productPage.tooltip.compatibility")}>
                                            <i className="fa-solid fa-circle-info" />
                                        </Tooltip>
                                    </div>

                                    <div className={classes.sectionBox}>
                                        <RolloutField name="rolloutPercentage" />
                                        <Tooltip title={t("modals:buildConfig.tooltip.rollout")}>
                                            <i className="fa-solid fa-circle-info" />
                                        </Tooltip>
                                    </div>

                                    <Box className={classes.sectionBox}>
                                        <ControlledSelectField
                                            name="configOverride.buildVariant"
                                            label={t("modals:buildConfig.input.buildVariant.caption")}
                                            options={buildVariantOptions}
                                            fullWidth
                                        />
                                        <BuildVariantLabel />
                                    </Box>
                                </Box>
                            </CollapsibleSectionDetails>
                        </CollapsibleProvider>
                    </form>
                </Modal>
            )}
        />
    );
};
